"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RequestAccess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="request-access" ref={ref} className="py-20 px-4 bg-[hsl(45_20%_98%)]">
      <div className="max-w-4xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[hsl(215_20%_13%)] mb-4">
            Request Access
          </h2>
          <p className="text-[hsl(210_10%_40%)] text-lg max-w-2xl mx-auto">
            Fill out the form below to gain access to our Supplier & Buyer Portals
          </p>
        </div>

        <FormProvider className={`bg-white rounded-sm shadow-soft p-8 md:p-12 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} formId="request_access_form_supplierandbuyerportals" sectionName="request-access" defaultValues={{
        full_name: "",
        company_name: "",
        email: "",
        phone: "",
        role: "",
        access_reason: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        company_name: {
          required: true,
          minLength: 2,
          maxLength: 200
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        role: {
          required: true
        },
        access_reason: {
          required: true,
          minLength: 10,
          maxLength: 500
        }
      }} submitText="Submit Request" successMessage="Thank you! Your access request has been submitted successfully. Our team will review it and get back to you shortly." errorMessage="We couldn't submit your request. Please check your information and try again.">
          <RequestAccessForm />
        </FormProvider>
      </div>
    </section>;
}
function RequestAccessForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-[hsl(65_70%_95%)] mb-6">
          <svg className="size-8 text-[hsl(65_70%_50%)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-[hsl(215_20%_13%)] text-lg font-medium mb-6">{successMessage}</p>
        <Button onClick={resetForm} className="btn-primary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 100,
            message: "Name must not exceed 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[hsl(215_20%_13%)] font-display font-semibold">
                  Full Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Jane Doe" className="border-[hsl(215_70%_15%)] focus-visible:border-[hsl(65_70%_50%)] focus-visible:ring-[hsl(65_70%_50%)] transition-all duration-150" {...field} />
                </FormControl>
                <FormDescription className="text-[hsl(210_10%_40%)] text-sm">
                  Enter your first and last name.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="company_name" rules={{
          required: "Company name is required",
          minLength: {
            value: 2,
            message: "Company name must be at least 2 characters"
          },
          maxLength: {
            value: 200,
            message: "Company name must not exceed 200 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[hsl(215_20%_13%)] font-display font-semibold">
                  Company Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Acme Trading Ltd." className="border-[hsl(215_70%_15%)] focus-visible:border-[hsl(65_70%_50%)] focus-visible:ring-[hsl(65_70%_50%)] transition-all duration-150" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[hsl(215_20%_13%)] font-display font-semibold">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane.doe@example.com" className="border-[hsl(215_70%_15%)] focus-visible:border-[hsl(65_70%_50%)] focus-visible:ring-[hsl(65_70%_50%)] transition-all duration-150" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[\d\s\-\+\(\)]+$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[hsl(215_20%_13%)] font-display font-semibold">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+971 55 123 4567" className="border-[hsl(215_70%_15%)] focus-visible:border-[hsl(65_70%_50%)] focus-visible:ring-[hsl(65_70%_50%)] transition-all duration-150" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="role" rules={{
        required: "Please select your role"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(215_20%_13%)] font-display font-semibold">
                Your Role
              </FormLabel>
              <FormControl>
                <select className="flex h-9 w-full rounded-md border border-[hsl(215_70%_15%)] bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-[hsl(65_70%_50%)] focus-visible:ring-[hsl(65_70%_50%)] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                  <option value="">Select your role</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Buyer">Buyer</option>
                  <option value="Distributor">Distributor</option>
                  <option value="Other">Other</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="access_reason" rules={{
        required: "Please provide a reason for requesting access",
        minLength: {
          value: 10,
          message: "Please provide at least 10 characters"
        },
        maxLength: {
          value: 500,
          message: "Reason must not exceed 500 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(215_20%_13%)] font-display font-semibold">
                Reason for Requesting Access
              </FormLabel>
              <FormControl>
                <textarea rows={5} placeholder="Briefly describe why you need access to the Supplier & Buyer Portals." className="flex w-full rounded-md border border-[hsl(215_70%_15%)] bg-transparent px-3 py-2 text-base shadow-xs transition-all outline-none focus-visible:border-[hsl(65_70%_50%)] focus-visible:ring-[hsl(65_70%_50%)] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none" {...field} />
              </FormControl>
              <FormDescription className="text-[hsl(210_10%_40%)] text-sm">
                Provide details such as the commodities you deal with or the services you require.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="btn-primary w-full md:w-auto md:px-12">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Submitting...
              </> : 'Submit Request'}
          </Button>
        </div>
      </div>
    </Form>;
}